import { ref, getDownloadURL, deleteObject, uploadBytesResumable } from "firebase/storage";
import { getFirebaseStorage } from "./firebase-ultra-safe";

export interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

const EXTENSION_CONTENT_TYPE_MAP: Record<string, string> = {
  ".mp3": "audio/mpeg",
  ".wav": "audio/wav",
  ".m4a": "audio/mp4",
  ".aac": "audio/aac",
  ".3gp": "audio/3gpp",
  ".ogg": "audio/ogg",
  ".webm": "audio/webm",
  ".mp4": "video/mp4",
  ".mov": "video/quicktime",
  ".avi": "video/x-msvideo",
};

function guessContentType(file: File): string | undefined {
  if (file.type) {
    return file.type;
  }

  const lowerName = file.name.toLowerCase();
  for (const [ext, type] of Object.entries(EXTENSION_CONTENT_TYPE_MAP)) {
    if (lowerName.endsWith(ext)) {
      return type;
    }
  }

  return undefined;
}

// 이미지 업로드
export async function uploadImage(
  file: File,
  path: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  try {
    // Storage 인스턴스 가져오기 (함수 호출 시마다)
    const storage = getFirebaseStorage();
    if (!storage) {
      console.error("❌ Firebase Storage가 초기화되지 않았습니다.");
      return {
        success: false,
        error: "Storage 서비스를 사용할 수 없습니다.",
      };
    }

    // 파일 타입 검증
    if (!file.type.startsWith("image/")) {
      return {
        success: false,
        error: "이미지 파일만 업로드 가능합니다.",
      };
    }

    // 파일 크기 검증 (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return {
        success: false,
        error: "파일 크기는 5MB를 초과할 수 없습니다.",
      };
    }

    // Firebase Storage 참조 생성
    const storageRef = ref(storage, path);

    // 업로드 실행 (진행률 추적 포함)
    const contentType = guessContentType(file) || "application/octet-stream";
    if (!file.type) {
      console.log("ℹ️ MIME 타입이 비어 있어 확장자로 추론합니다:", {
        fileName: file.name,
        contentType,
      });
    }

    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType,
    });

    // 진행률 콜백이 있다면 실행
    if (onProgress) {
      uploadTask.on("state_changed", snapshot => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress({
          loaded: snapshot.bytesTransferred,
          total: snapshot.totalBytes,
          percentage: progress,
        });
      });
    }

    // 업로드 실행
    const snapshot = await uploadTask;

    // 다운로드 URL 가져오기
    const downloadURL = await getDownloadURL(snapshot.ref);

    return {
      success: true,
      url: downloadURL,
    };
  } catch (error) {
    console.error("이미지 업로드 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "이미지 업로드에 실패했습니다.",
    };
  }
}

// 여러 이미지 업로드
export async function uploadMultipleImages(
  files: File[],
  basePath: string,
  onProgress?: (index: number, progress: UploadProgress) => void
): Promise<{ success: boolean; urls: string[]; errors: string[] }> {
  const results = await Promise.allSettled(
    files.map((file, index) => {
      const timestamp = Date.now();
      const fileName = `${timestamp}_${index}_${file.name}`;
      const path = `${basePath}/${fileName}`;

      return uploadImage(file, path, progress => {
        onProgress?.(index, progress);
      });
    })
  );

  const urls: string[] = [];
  const errors: string[] = [];

  results.forEach((result, index) => {
    if (result.status === "fulfilled" && result.value.success) {
      urls.push(result.value.url!);
    } else {
      const error =
        result.status === "rejected"
          ? result.reason
          : result.value.error || "업로드 실패";
      errors.push(`이미지 ${index + 1}: ${error}`);
    }
  });

  return {
    success: urls.length > 0,
    urls,
    errors,
  };
}

// 이미지 삭제
export async function deleteImage(
  url: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const storage = getFirebaseStorage();
    if (!storage) {
      return {
        success: false,
        error: "Storage 서비스를 사용할 수 없습니다.",
      };
    }
    const imageRef = ref(storage, url);
    await deleteObject(imageRef);
    return { success: true };
  } catch (error) {
    console.error("이미지 삭제 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : "이미지 삭제에 실패했습니다.",
    };
  }
}

// 판매글 이미지 업로드 (여러 이미지)
export async function uploadImages(
  files: File[],
  onProgress?: (index: number, progress: UploadProgress) => void
): Promise<{ success: boolean; urls: string[]; errors: string[] }> {
  const basePath = `items/${Date.now()}`;
  return uploadMultipleImages(files, basePath, onProgress);
}

// 이미지 URL에서 파일명 추출
export function getFileNameFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    return pathname.split("/").pop() || "";
  } catch {
    return "";
  }
}

// 오디오/비디오 파일 업로드 (전문가 피드백용)
export async function uploadMediaFile(
  file: File,
  uid: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> {
  try {
    console.log("🔍 Storage 초기화 시도...");
    
    // Storage 인스턴스 가져오기 (함수 호출 시마다)
    const storage = getFirebaseStorage();
    if (!storage) {
      console.error("❌ Firebase Storage가 초기화되지 않았습니다.");
      return {
        success: false,
        error: "Storage 서비스를 사용할 수 없습니다. 페이지를 새로고침해주세요.",
      };
    }

    console.log("✅ Storage 초기화 성공");
    
    // Firebase Auth 인증 확인 및 필요시 Custom Token으로 로그인
    try {
      const { getFirebaseAuth } = await import("./firebase-ultra-safe");
      const { signInWithCustomToken } = await import("firebase/auth");
      const auth = getFirebaseAuth();
      
      if (auth) {
        // 현재 인증 상태 확인
        let currentUser = auth.currentUser;
        
        // 인증된 사용자가 없으면 서버에서 Custom Token 요청 (NextAuth 사용자용)
        if (!currentUser) {
          console.log("🔐 Firebase Auth 인증 없음 - Custom Token 요청 시도...");
          try {
            // 서버 API에서 Custom Token 생성 요청
            const tokenResponse = await fetch("/api/auth/firebase-token", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ uid }),
            });
            
            if (tokenResponse.ok) {
              const { customToken } = await tokenResponse.json();
              if (customToken) {
                const userCredential = await signInWithCustomToken(auth, customToken);
                currentUser = userCredential.user;
                console.log("✅ Firebase Auth Custom Token 로그인 성공:", currentUser.uid);
              } else {
                console.warn("⚠️ Custom Token이 반환되지 않음");
              }
            } else {
              let errorText = "";
              let errorDetails: any = null;
              try {
                errorText = await tokenResponse.text();
                try {
                  errorDetails = JSON.parse(errorText);
                } catch {
                  // JSON 파싱 실패 시 그대로 사용
                }
              } catch {
                errorText = "알 수 없는 오류";
              }
              
              console.error("❌ Custom Token 요청 실패:", {
                status: tokenResponse.status,
                statusText: tokenResponse.statusText,
                error: errorText,
                details: errorDetails,
              });
              
              const errorMessage = errorDetails?.error || errorDetails?.details || errorText || "Firebase 인증에 실패했습니다.";
              return {
                success: false,
                error: errorMessage,
              };
            }
          } catch (authError: any) {
            console.error("❌ Firebase Auth Custom Token 로그인 실패:", authError);
            return {
              success: false,
              error: "업로드 권한이 없습니다. Firebase Storage 규칙을 확인해주세요.",
            };
          }
        } else {
          console.log("✅ Firebase Auth 인증됨:", currentUser.uid);
        }
      }
    } catch (authError) {
      console.warn("⚠️ Firebase Auth 확인 실패:", authError);
      // Auth 확인 실패해도 계속 진행
    }

    // 파일 타입 검증
    const isAudio = file.type.startsWith("audio/");
    const isVideo = file.type.startsWith("video/");
    
    // 모바일 녹음 파일 확장자도 허용 (MIME 타입이 없는 경우)
    const fileExtension = file.name.toLowerCase();
    const audioExtensions = ['.mp3', '.wav', '.m4a', '.aac', '.3gp', '.ogg', '.webm'];
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm'];
    const hasValidExtension = [...audioExtensions, ...videoExtensions].some(ext => fileExtension.endsWith(ext));
    
    console.log("📄 파일 타입 확인:", { fileType: file.type, isAudio, isVideo, fileExtension, hasValidExtension });
    
    if (!isAudio && !isVideo && !hasValidExtension) {
      console.warn("⚠️ 지원하지 않는 파일 타입:", file.type);
      return {
        success: false,
        error: "오디오(mp3, wav, m4a, aac) 또는 비디오 파일만 업로드 가능합니다.",
      };
    }

    // 파일 크기 검증 (100MB)
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      console.warn("⚠️ 파일 크기 초과:", file.size);
      return {
        success: false,
        error: "파일 크기는 100MB를 초과할 수 없습니다.",
      };
    }

    // Firebase Storage 참조 생성
    // 파일명에서 공백과 특수문자 제거 (CORS 및 URL 인코딩 문제 방지)
    const sanitizeFileName = (name: string): string => {
      // 확장자 추출
      const lastDotIndex = name.lastIndexOf('.');
      const extension = lastDotIndex > -1 ? name.substring(lastDotIndex) : '';
      const nameWithoutExt = lastDotIndex > -1 ? name.substring(0, lastDotIndex) : name;
      
      // 공백 제거, 특수문자를 언더스코어로 변환
      const sanitized = nameWithoutExt
        .replace(/\s+/g, '_') // 공백을 언더스코어로
        .replace(/[^a-zA-Z0-9_\-가-힣]/g, '_') // 특수문자를 언더스코어로 (한글, 영문, 숫자, 언더스코어, 하이픈만 허용)
        .replace(/_+/g, '_') // 연속된 언더스코어를 하나로
        .replace(/^_|_$/g, ''); // 앞뒤 언더스코어 제거
      
      return sanitized + extension;
    };
    
    const timestamp = Date.now();
    const sanitizedFileName = sanitizeFileName(file.name);
    const fileName = `${timestamp}_${sanitizedFileName}`;
    const path = `expert-feedback/${uid}/${fileName}`;
    console.log("📁 Storage 경로:", path);
    console.log("📝 원본 파일명:", file.name);
    console.log("📝 정리된 파일명:", sanitizedFileName);
    
    const storageRef = ref(storage, path);

    // 업로드 실행 (진행률 추적 포함)
    console.log("🚀 업로드 시작...");
    const contentType = guessContentType(file) || "application/octet-stream";
    if (!file.type) {
      console.log("ℹ️ MIME 타입이 비어 있어 확장자로 추론합니다:", {
        fileName: file.name,
        contentType,
      });
    }

    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType,
    });

    // 진행률 콜백이 있다면 실행
    if (onProgress) {
      uploadTask.on("state_changed", 
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          onProgress({
            loaded: snapshot.bytesTransferred,
            total: snapshot.totalBytes,
            percentage: progress,
          });
        },
        (error) => {
          console.error("❌ 업로드 진행 중 오류:", error);
        }
      );
    }

    // 업로드 실행
    const snapshot = await uploadTask;
    console.log("✅ 업로드 완료:", snapshot);

    // 다운로드 URL 가져오기
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log("✅ 다운로드 URL 획득:", downloadURL);

    return {
      success: true,
      url: downloadURL,
    };
  } catch (error) {
    console.error("❌ 미디어 파일 업로드 실패:", error);
    
    // 더 상세한 에러 메시지 제공
    let errorMessage = "파일 업로드에 실패했습니다.";
    if (error instanceof Error) {
      errorMessage = error.message;
      // Firebase Storage 권한 오류 체크
      if (error.message.includes("permission") || error.message.includes("unauthorized")) {
        errorMessage = "업로드 권한이 없습니다. Firebase Storage 규칙을 확인해주세요.";
      } else if (error.message.includes("storage/unauthorized")) {
        errorMessage = "인증되지 않았습니다. 로그인 후 다시 시도해주세요.";
      }
    }
    
    return {
      success: false,
      error: errorMessage,
    };
  }
}

const REALTIME_AUDIO_MAX_BYTES = 20 * 1024 * 1024;

function sanitizeUploadFileName(name: string): string {
  const lastDotIndex = name.lastIndexOf(".");
  const extension = lastDotIndex > -1 ? name.substring(lastDotIndex) : "";
  const nameWithoutExt =
    lastDotIndex > -1 ? name.substring(0, lastDotIndex) : name;
  const sanitized = nameWithoutExt
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_\-가-힣]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  return `${sanitized}${extension}`;
}

export type RealtimeAudioUploadResult = UploadResult & {
  storagePath?: string;
  fileName?: string;
};

/** 실시간 피드백 음원(MP3 ≤20MB) 업로드 */
export async function uploadRealtimeFeedbackAudio(
  file: File,
  uid: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<RealtimeAudioUploadResult> {
  const { validateRealtimeFeedbackAudio } = await import(
    "@/lib/utils/fileValidation"
  );
  const validation = validateRealtimeFeedbackAudio(file);
  if (!validation.valid) {
    return { success: false, error: validation.error };
  }

  try {
    const storage = getFirebaseStorage();
    if (!storage) {
      return {
        success: false,
        error: "Storage 서비스를 사용할 수 없습니다.",
      };
    }

    const { getFirebaseAuth } = await import("./firebase-ultra-safe");
    const auth = getFirebaseAuth();
    if (!auth?.currentUser) {
      return { success: false, error: "로그인이 필요합니다." };
    }

    const timestamp = Date.now();
    const fileName = `${timestamp}_${sanitizeUploadFileName(file.name)}`;
    const storagePath = `realtime-feedback-audio/${uid}/${fileName}`;
    const storageRef = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: "audio/mpeg",
    });

    if (onProgress) {
      uploadTask.on("state_changed", snapshot => {
        onProgress({
          loaded: snapshot.bytesTransferred,
          total: snapshot.totalBytes,
          percentage: (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
        });
      });
    }

    const snapshot = await uploadTask;
    const url = await getDownloadURL(snapshot.ref);
    return {
      success: true,
      url,
      storagePath,
      fileName: file.name,
    };
  } catch (error) {
    console.error("실시간 피드백 음원 업로드 실패:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "음원 업로드에 실패했습니다.",
    };
  }
}

export { REALTIME_AUDIO_MAX_BYTES };
